import noData from 'assets/images/noData.png';
import classNames from 'classnames';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getUserData } from 'store/selectors/user';
import {
  useAddSurveyAnswerMutation,
  useGetFeedbackGroupQuery,
  useGetSurveyAnswerQuery,
  useUpdateSurveyAnswerMutation,
} from 'store/services/feedbackGroup';
import Button from 'style-guide/Button';
import Form from 'style-guide/Form';
import Image from 'style-guide/Image';
import Radio from 'style-guide/Radio';
import { message } from 'style-guide/ToastMessages';
import Text from 'style-guide/Typography/Text';
import Title from 'style-guide/Typography/Title';
import { SurveyViewSkeleton } from '../FeedbackQuestion/SurveySkeleton';
import FeedbackQuestionViewWrapper from './style/FeedbackQuestionViewWrapper';

const FeedbackQuestionView = ({ groupId, currentSurvey }) => {
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: [],
  });
  const { chapterId } = useParams();
  const user = useSelector(getUserData);
  const { data, isLoading } = useGetFeedbackGroupQuery({ groupId, userId: user.id });
  const { data: surveyAnswerData } = useGetSurveyAnswerQuery({ chapterId: +chapterId, userId: +user.id });
  const [addSurveyAnswer, { isSuccess }] = useAddSurveyAnswerMutation();
  const [updateSurveyAnswer, { isSuccess: isUpdateSuccess }] = useUpdateSurveyAnswerMutation();
  const handleRadioChange = (questionIndex, value) => {
    setValue(`questions[${questionIndex}].selected`, value, { shouldDirty: true });
  };
  useEffect(() => {
    if (data?.feedbackQuestions?.length && surveyAnswerData) {
      const initialValues = {
        questions: data.feedbackQuestions.map((q) => ({
          selected: surveyAnswerData.find((sa) => sa.feedbackQuestionId === q.id)?.point ?? '',
        })),
      };
      reset(initialValues);
    }
  }, [data, surveyAnswerData, reset]);
  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      message.success(`Your answer successfully has been ${isSuccess ? 'created' : 'updated'}`);
    }
  }, [isSuccess, isUpdateSuccess]);
  const onFormSubmit = (formData) => {
    const answers = formData.questions.map((question, index) => {
      const selectedPoint = question.selected;
      const feedbackQuestionId = data.feedbackQuestions[index].id;
      const existingAnswer = surveyAnswerData?.find((answer) => answer?.feedbackQuestionId === feedbackQuestionId);
      return {
        feedbackQuestionId,
        point: selectedPoint,
        id: existingAnswer?.id,
      };
    });
    const isValidPoint = (point) => point !== null && point !== '';
    const addAnswers = answers.filter((answer) => !answer.id && isValidPoint(answer.point));
    const updateAnswers = answers.filter((answer) => answer.id && answer.point);
    if (addAnswers.length > 0) {
      addSurveyAnswer({ answers: addAnswers, userId: user.id, chapterId: +chapterId });
    }
    if (updateAnswers.length > 0) {
      updateSurveyAnswer({ answers: updateAnswers, userId: user.id, chapterId: +chapterId });
    }
  };
  const hasSelectedAnswer = data?.feedbackQuestions.some((q) =>
    surveyAnswerData?.some((sa) => sa.feedbackQuestionId === q.id)
  );
  const validSurvey =
    (currentSurvey && Object.keys(currentSurvey)?.length > 0) || (data && Object.keys(data)?.length > 0);

  return (
    <>
      {isLoading && <SurveyViewSkeleton />}
      {(!isLoading && !validSurvey) || !currentSurvey?.feedbackQuestions?.length ? (
        <div className='d-flex justify-content-center mt--80'>
          <Image src={noData} />
        </div>
      ) : (
        <Form>
          {currentSurvey?.feedbackQuestions.map((item, questionIndex) => {
            const points = Array.from(new Set(item.questionAnswers.flatMap((qa) => [qa.rangeFrom, qa.rangeTo]))).sort(
              (a, b) => a - b
            );
            return (
              <FeedbackQuestionViewWrapper className='likert-scale-container mt--80 mb--32 mh--16' key={item.id}>
                <div className='d-flex mb--16'>
                  <Title variant={5} className='mr--8'>
                    Type :
                  </Title>
                  <Text variant={5} className='mr--8'>
                    {item?.type}
                  </Text>
                </div>
                <Text>{item?.question}</Text>
                <div className='number-container mt--24'>
                  {points.map((point, index) => (
                    <div
                      key={point}
                      className={classNames('segment', {
                        'segment-last': index === points.length - 1,
                        'segment-first': index === 0,
                      })}
                    >
                      {point}
                    </div>
                  ))}
                </div>
                <div className='scale-line'>
                  {points.map((point, index) => (
                    <Controller
                      key={point}
                      name={`questions[${questionIndex}].selected`}
                      control={control}
                      render={({ field }) => (
                        <Radio
                          {...field}
                          value={point}
                          checked={field.value === point}
                          onChange={() => handleRadioChange(questionIndex, point)}
                          className={classNames('marker', {
                            'segment-last': index === points.length - 1,
                            'segment-first': index === 0,
                          })}
                          style={{ left: `${(index / (points.length - 1)) * 100}%` }}
                        />
                      )}
                    />
                  ))}
                </div>
                <div className=' row mt--16 d-flex justify-content-between'>
                  {item.questionAnswers
                    .slice()
                    .sort((a, b) => a.rangeTo - b.rangeTo)
                    .map((answer, index) => {
                      let position;
                      if (answer.rangeFrom === answer.rangeTo) {
                        if (index === item.questionAnswers.length - 1) {
                          position = 'flex-end';
                        } else {
                          position = 'flex-start';
                        }
                      } else {
                        position = 'center';
                      }
                      const pointesCount = points.length;
                      const col = Math.round(12 / pointesCount);
                      return (
                        <div style={{ justifyContent: position }} className={`col-${col} d-flex`} key={answer.id}>
                          <Text className='section-text d-flex'>{answer.answer}</Text>
                        </div>
                      );
                    })}
                </div>
              </FeedbackQuestionViewWrapper>
            );
          })}
          <div className='d-flex justify-content-end mb--16'>
            <Button variant='primary' className='mt--16' onClick={handleSubmit(onFormSubmit)} disabled={!isDirty}>
              {hasSelectedAnswer ? 'Update' : 'Save'}
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};
export default FeedbackQuestionView;
